import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const links = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Edit Requests", path: "/edits" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const hideOnHome = isHome && !scrolled;
  const light = true; // always dark nav text on a light bar, for readability on every page

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const headerBg = light
    ? "bg-white/95 backdrop-blur-xl border-b border-gray-200 py-4"
    : scrolled
    ? "bg-charcoal/90 backdrop-blur-xl border-b border-white/10 py-3"
    : "bg-transparent py-5";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        hideOnHome ? "opacity-0 -translate-y-full pointer-events-none" : "opacity-100 translate-y-0"
      } ${headerBg}`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo dark={!light} />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const active = location.pathname === l.path;
            const linkClass = light
              ? active
                ? "text-charcoal font-semibold"
                : "text-charcoal/60 hover:text-charcoal"
              : active
              ? "text-sky-brand"
              : "text-white/80 hover:text-white";
            return (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.name.toLowerCase()}`}
                className={`font-display text-sm font-medium tracking-wide transition-colors duration-200 ${linkClass}`}
              >
                {l.name}
              </Link>
            );
          })}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className={`font-display bg-sky-brand text-charcoal transition-colors duration-300 font-semibold px-6 py-2.5 rounded-full text-sm ${
              light ? "hover:bg-charcoal hover:text-white" : "hover:bg-white"
            }`}
          >
            Free Preview
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden ${light ? "text-charcoal" : "text-white"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className={`md:hidden mt-3 border-t ${light ? "bg-white border-gray-200" : "bg-charcoal border-white/10"}`}
        >
          <div className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => {
              const active = location.pathname === l.path;
              const mClass = light
                ? active
                  ? "text-charcoal font-semibold"
                  : "text-charcoal/70"
                : active
                ? "text-sky-brand"
                : "text-white/90";
              return (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`mobile-nav-link-${l.name.toLowerCase()}`}
                  className={`font-display text-lg font-medium ${mClass}`}
                >
                  {l.name}
                </Link>
              );
            })}
            <Link
              to="/contact"
              className="font-display bg-sky-brand text-charcoal font-semibold px-6 py-3 rounded-full text-center mt-2"
            >
              Get Your Free Preview
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
